import { FC, ReactNode } from 'react';
import { animated, useTransition } from '@react-spring/web';

/** Default offset (px) for entrance/exit slide */
const SLOT_OFFSET = 150;

/** Underdamped spring config — produces bounce on settle (damping ≈ 0.52) */
const SPRING_CONFIG = { tension: 300, friction: 25 };

export interface IAnimatedSlotProps {
	children: ReactNode;
	/** Entrance/exit direction: bottom-to-top or right-to-left */
	direction?: 'up' | 'left' | 'right';
	delay?: number;
	slotOffset?: number;
}

/**
 * Single-slot entrance/exit animator via useTransition.
 * Keeps content mounted and visible during the leave spring,
 * then unmounts automatically once the animation settles.
 */
export const AnimatedSlot: FC<IAnimatedSlotProps> = ({
	children,
	direction = 'up',
	delay,
	slotOffset = SLOT_OFFSET,
}) => {
	/** Wrap children in array with static key for proper transition tracking */
	const items = children ? [{ key: 'slot', content: children }] : [];

	/** Animation configuration for each direction */
	const animationConfig = {
		up: {
			offset: { y: slotOffset, opacity: 0 },
			rest: { y: 0, opacity: 1 },
		},
		left: {
			offset: { x: slotOffset, opacity: 0 },
			rest: { x: 0, opacity: 1 },
		},
		right: {
			offset: { x: -slotOffset, opacity: 0 },
			rest: { x: 0, opacity: 1 },
		},
	};

	const { offset: offsetStyle, rest: restStyle } = animationConfig[direction];

	const transitions = useTransition(items, {
		keys: (item) => item.key,
		from: offsetStyle,
		enter: restStyle,
		leave: offsetStyle,
		config: SPRING_CONFIG,
		delay,
	});

	return (
		<>
			{transitions((style, item) => (
				<animated.div style={style}>{item.content}</animated.div>
			))}
		</>
	);
};
